import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import {
  ENVIRONMENT_URL,
  STYTCH_CONNECTED_APPS_DOMAIN,
  STYTCH_PUBLIC_TOKEN,
} from '@backrs_ui/grow-ui/src/settings';
import { setPostLoginRedirect } from '@backrs_ui/grow-ui/src/utils/postLoginRedirect';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Box, CircularProgress, SxProps, Theme, Typography } from '@mui/material';
import { IdentityProvider, StytchProvider, createStytchClient } from '@stytch/react';

const CONNECT_APP_STRINGS = appStrings.modules.connectApp;

type AttestationState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; token: string; profileId: string };

// Created on first visit rather than at module load, so the app doesn't boot a
// Stytch client for users who never hit the consent page.
let stytchClient: ReturnType<typeof createStytchClient> | null = null;

const getStytchClient = () => {
  stytchClient ??= createStytchClient(
    STYTCH_PUBLIC_TOKEN,
    STYTCH_CONNECTED_APPS_DOMAIN
      ? { endpointOptions: { apiDomain: STYTCH_CONNECTED_APPS_DOMAIN } }
      : undefined,
  );

  return stytchClient;
};

/*
 * The OAuth authorize page for connecting a Backrs account to an MCP client
 * (ChatGPT/Claude). Stytch redirects here with the OAuth params in the URL;
 * IdentityProvider reads them itself, exchanges our attestation token for a
 * Stytch session, renders the consent screen, and redirects back to the client.
 */
export const ConnectAppAuthorizePageContainer = () => {
  const { loggedIn } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<AttestationState>({ status: 'loading' });

  useEffect(() => {
    if (!loggedIn) return undefined;

    let ignore = false;

    const fetchAttestation = async () => {
      try {
        const res = await fetch(`${ENVIRONMENT_URL}/auth/mcp-attestation`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getBackrsToken()}` },
        });

        if (!res.ok) throw new Error(`mcp-attestation responded ${res.status}`);

        const body = (await res.json()) as { token?: string; profile_id?: string };

        if (!body.token || !body.profile_id) throw new Error('mcp-attestation body incomplete');

        // Clear any stale Stytch session so IdentityProvider's pre-attest revoke
        // doesn't fire against an already-gone server session (surfaces as an
        // uncaught error). forceClear drops local state even if the call 404s.
        await getStytchClient()
          .session.revoke({ forceClear: true })
          .catch(() => undefined);

        if (!ignore) setState({ status: 'ready', token: body.token, profileId: body.profile_id });
      } catch (err) {
        console.error('[ConnectAppAuthorizePageContainer] attestation fetch failed', err);

        if (!ignore) setState({ status: 'error' });
      }
    };

    void fetchAttestation();

    return () => {
      ignore = true;
    };
  }, [loggedIn]);

  if (!loggedIn) {
    // Survives the OTP hop and the cross-origin OAuth round-trip, unlike router state.
    setPostLoginRedirect(`${location.pathname}${location.search}`);

    return <Navigate to={APP_ROUTE.LOGIN} replace />;
  }

  if (state.status === 'error') {
    return (
      <Box sx={containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={messageBlockSx}>
            <Typography variant="text-lg-semibold">
              {CONNECT_APP_STRINGS.genericError.title}
            </Typography>
            <Typography variant="text-md-medium">
              {CONNECT_APP_STRINGS.genericError.subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (state.status === 'loading') {
    return (
      <Box sx={containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={loadingContainerSx}>
            <CircularProgress size={24} />
            <Typography variant="text-md-bold">{CONNECT_APP_STRINGS.loading}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <Box sx={contentWrapperSx} id="connect-app-consent">
        <StytchProvider stytch={getStytchClient()}>
          <IdentityProvider
            authTokenParams={{ trustedAuthToken: state.token, tokenProfileID: state.profileId }}
            callbacks={{
              onError: ({ message }) => {
                // The SDK fires a transient no-session error on mount while its attest
                // call is still in flight, then recovers once the session lands.
                if (message.startsWith('There is no session currently available')) return;

                console.error('[ConnectAppAuthorizePageContainer] consent error', message);
                setState({ status: 'error' });
              },
            }}
          />
        </StytchProvider>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  minHeight: '100vh',
  width: '100vw',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xl'],
});

const contentWrapperSx: SxProps<Theme> = {
  maxWidth: '500px',
  width: '100%',
  textAlign: 'center',
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const messageBlockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});
